import { Component, inject } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { SurveyService } from '../../services/survey.service';
import { SURVEY_CONFIG, SurveyConfig } from '../../../assets/data/survey';
import { showAlert } from '../../tools/alerts';
import { finalize } from 'rxjs';
import { Survey } from '../../models/survey';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-survey',
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, TranslateModule],
  templateUrl: './survey.component.html',
  styleUrl: './survey.component.css'
})
export class SurveyComponent {

  private surveyService = inject(SurveyService);
  private fb = inject(FormBuilder);
  private translate = inject(TranslateService);

  surveyForm: FormGroup = this.fb.group({});
  isSubmitting: boolean = false;
  config: SurveyConfig = SURVEY_CONFIG;
  currentStep: number = 0;

  constructor() {
    this.initForm();
  }

  get totalSteps() {
    return this.config.sections.length;
  }

  get currentSection() {
    return this.config.sections[this.currentStep];
  }

  initForm() {
    this.config.sections.forEach(section => {
      section.questions.forEach(question => {
        let validators = [];
        if (question.required) {
          validators.push(Validators.required);
        }

        let initialValue: any = null;
        if (question.type === 'multi-select') {
          initialValue = [];
        } else if (question.type === 'text' || question.type === 'textarea' || question.type === 'select') {
          initialValue = '';
        }

        const control = this.fb.control(initialValue, validators);
        this.surveyForm.addControl(question.key, control);
      });
    });
  }

  isQuestionVisible(question: any): boolean {
    if (!question.dependsOn) return true;

    const dependencyControl = this.surveyForm.get(question.dependsOn.key);
    if (!dependencyControl) return true;

    return dependencyControl.value === question.dependsOn.value;
  }

  nextStep() {
    if (this.currentStep < this.totalSteps - 1) {
      if (this.validateCurrentStep()) {
        this.currentStep++;
        window.scrollTo(0, 0);
      } else {
        this.markCurrentStepTouched();
      }
    }
  }

  prevStep() {
    if (this.currentStep > 0) {
      this.currentStep--;
      window.scrollTo(0, 0);
    }
  }

  validateCurrentStep(): boolean {
    const currentQuestions = this.currentSection.questions;
    let isValid = true;
    currentQuestions.forEach(q => {
      if (this.isQuestionVisible(q)) {
        const control = this.surveyForm.get(q.key);
        if (control && control.invalid) {
          isValid = false;
        }
      }
    });
    return isValid;
  }

  markCurrentStepTouched() {
    const currentQuestions = this.currentSection.questions;
    currentQuestions.forEach(q => {
      if (this.isQuestionVisible(q)) {
        this.surveyForm.get(q.key)?.markAsTouched();
      }
    });
  }

  onCheckboxChange(key: string, value: any, event: any) {
    const currentValues = this.surveyForm.get(key)?.value as any[] || [];
    if (event.target.checked) {
      this.surveyForm.patchValue({ [key]: [...currentValues, value] });
    } else {
      this.surveyForm.patchValue({ [key]: currentValues.filter(v => v !== value) });
    }
    this.surveyForm.get(key)?.markAsTouched();
  }

  setRating(key: string, rating: number) {
    this.surveyForm.patchValue({ [key]: rating });
    this.surveyForm.get(key)?.markAsTouched();
  }

  dropdownOpenRegex: { [key: string]: boolean } = {};
  searchTerms: { [key: string]: string } = {};

  toggleDropdown(key: string, show?: boolean) {
    if (show === undefined) {
      this.dropdownOpenRegex[key] = !this.dropdownOpenRegex[key];
    } else {
      this.dropdownOpenRegex[key] = show;
    }
  }

  getFilteredOptions(question: any) {
    const term = this.searchTerms[question.key] || '';
    if (!term) return question.options;
    return question.options.filter((opt: any) => {
      const translated = this.translate.instant(opt.label) || opt.label;
      return translated.toLowerCase().includes(term.toLowerCase());
    });
  }

  onSearch(key: string, event: any) {
    this.searchTerms[key] = event.target.value;
    this.dropdownOpenRegex[key] = true;
  }

  selectOption(question: any, option: any) {
    this.surveyForm.get(question.key)?.setValue(option.value);
    this.searchTerms[question.key] = this.translate.instant(option.label) || option.label;
    this.dropdownOpenRegex[question.key] = false;
  }

  clearSelection(question: any, event: Event) {
    event.stopPropagation();
    this.surveyForm.get(question.key)?.setValue(null);
    this.searchTerms[question.key] = '';
    this.dropdownOpenRegex[question.key] = true;
  }

  hasSelection(question: any): boolean {
    const val = this.surveyForm.get(question.key)?.value;
    return val !== null && val !== '' && val !== undefined;
  }

  getDisplayValue(question: any): string {
    const val = this.surveyForm.get(question.key)?.value;
    if (val && !this.searchTerms[question.key]) {
      const opt = question.options?.find((o: any) => o.value === val);
      if (opt) this.searchTerms[question.key] = this.translate.instant(opt.label) || opt.label;
    }
    return this.searchTerms[question.key] || '';
  }

  onSubmit() {
    if (this.surveyForm.invalid) {
      if (!this.validateCurrentStep()) {
        this.markCurrentStepTouched();
        return;
      }
    }

    this.isSubmitting = true;

    const formData = { ...this.surveyForm.value };

    const surveyData: Omit<Survey, 'id' | 'createdAt'> = {
      nacionalidad: formData.nacionalidad || null,
      residencia: formData.residencia || null,
      edad: formData.edad ? Number(formData.edad) : null,
      estado_civil: formData.estado_civil || null,
      genero: formData.genero || null,
      situacion_laboral: formData.situacion_laboral || null,
      rango_salarial: formData.rango_salarial || null,

      motivo_visita: formData.motivo_visita || null,

      satisfaccion: formData.satisfaccion !== null ? Number(formData.satisfaccion) : null,
      uso_info_turistica: formData.uso_info_turistica === true || formData.uso_info_turistica === 'true', // Ensure boolean
      feedback_info_turistica: formData.feedback_info_turistica || null,

      primera_vez_fuerteventura: formData.primera_vez_fuerteventura === true || formData.primera_vez_fuerteventura === 'true',
      primera_vez_la_oliva: formData.primera_vez_la_oliva === true || formData.primera_vez_la_oliva === 'true',
      maravillas_visitadas: Array.isArray(formData.maravillas_visitadas) ? formData.maravillas_visitadas : [],

      comentarios: formData.comentarios || null
    };

    console.log('Sending Survey Data:', surveyData);

    this.surveyService.saveSurvey(surveyData)
      .pipe(finalize(() => this.isSubmitting = false))
      .subscribe({
        next: (resp) => {
          showAlert(this.translate.instant('SURVEY.SUCCESS_TITLE'), this.translate.instant('SURVEY.SUCCESS_MSG'), 'success');
          this.resetForm();
        },
        error: (error) => {
          console.error('Error al guardar la encuesta:', error);
          showAlert(this.translate.instant('SURVEY.ERROR_TITLE'), this.translate.instant('SURVEY.ERROR_MSG'), 'error');
        }
      });
  }

  resetForm() {
    this.surveyForm.reset();
    this.searchTerms = {};
    this.dropdownOpenRegex = {};

    this.config.sections.forEach(s => s.questions.forEach(q => {
      if (q.type === 'multi-select') {
        this.surveyForm.get(q.key)?.setValue([]);
      } else if (q.type === 'text' || q.type === 'textarea' || q.type === 'select') {
        this.surveyForm.get(q.key)?.setValue('');
      } else {
        this.surveyForm.get(q.key)?.setValue(null);
      }
    }));

    this.currentStep = 0;
    window.scrollTo(0, 0);
  }
}
